export interface Variant {
  key: string;
  weight: number;
}

export interface Experiment {
  key: string;
  layer: string | null;
  range: [number, number];
  variants: Variant[];
  targeting?: Record<string, unknown>;
}

export interface Config {
  experiments: Experiment[];
  version: number;
}

/** @internal exported for testing */
export function hash(str: string, seed = 0): number {
  let h = seed;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 0x5bd1e995);
    h ^= h >>> 13;
    h = Math.imul(h, 0x5bd1e995);
  }
  h ^= h >>> 15;
  return h >>> 0;
}

/** @internal exported for testing */
export function bucket(userId: string, salt: string): number {
  return (hash(userId + salt) % 10000) / 10000;
}

export function computeVariant(
  userId: string,
  experimentKey: string,
  cfg: Config,
  userAttributes?: Record<string, unknown>,
): string | null {
  const exp = cfg.experiments.find((e) => e.key === experimentKey);
  if (!exp || exp.variants.length === 0) return null;

  if (exp.targeting) {
    for (const [key, expectedValue] of Object.entries(exp.targeting)) {
      if (userAttributes?.[key] !== expectedValue) return null;
    }
  }

  // Layer bucket determines if user is in this experiment's traffic range
  const layerBucket = bucket(userId, `__ns__${exp.layer ?? exp.key}`);
  if (layerBucket < exp.range[0] || layerBucket >= exp.range[1]) return null;

  const variantBucket = bucket(userId, `${experimentKey}:v`);
  const totalWeight = exp.variants.reduce((s, v) => s + v.weight, 0);
  let cumulative = 0;
  for (const v of exp.variants) {
    cumulative += v.weight / totalWeight;
    if (variantBucket < cumulative) return v.key;
  }
  return exp.variants[exp.variants.length - 1].key;
}

export async function fetchConfig(opts: {
  projectKey: string;
  apiBase: string;
  timeoutMs?: number;
}): Promise<Config | null> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), opts.timeoutMs ?? 5000);

    const res = await fetch(`${opts.apiBase}/api/v1/ab/config`, {
      headers: { Authorization: `Bearer ${opts.projectKey}` },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      console.error(`[UserJourneys AB] Config fetch failed: ${res.status}`);
      return null;
    }

    const data = await res.json();

    if (!data.experiments || !Array.isArray(data.experiments)) {
      console.error('[UserJourneys AB] Invalid config response');
      return null;
    }

    return data;
  } catch (err) {
    console.error('[UserJourneys AB] Fetch config failed:', err);
    return null;
  }
}
